import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../api/axios';
import { downloadBackup } from '../../../api/backups';
import { REGISTRY_TARGET, runnerApi, errorText, type BackupRow, type S3Info } from '../../../api/migrationRunner';
import Backups from '../../Backups';
import { Alert, Btn, Choice, Dialog, ErrorBox, Panel, Pill, Spinner, bytes, when } from './ui';
import { useApprovalGate } from './ApprovalGate';

interface CompanyLite { id: string; company_name: string; database_name: string | null }

const WHY: Record<string, string> = {
  pre_migration: 'Before a migration',
  pre_restore: 'Before a restore',
  pre_query: 'Before a console write',
  manual: 'Manual',
  scheduled: 'Scheduled',
  emergency: 'Emergency',
};

/**
 * Backups and restore (approved mockup, "Backups and restore"): verified
 * dumps of every company and the registry, on S3 when configured; restore into
 * a copy, or over the live database with a typed confirmation and a fresh
 * backup first.
 */
const BackupsTab: React.FC<{ isOwner: boolean }> = ({ isOwner }) => {
  const [rows, setRows] = useState<BackupRow[] | null>(null);
  const [s3, setS3] = useState<S3Info | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<CompanyLite[]>([]);
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');
  const [restoreOf, setRestoreOf] = useState<BackupRow | null>(null);
  const [showFiles, setShowFiles] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await runnerApi.backups();
      setRows(r.backups);
      setS3(r.s3);
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not list backups'));
      setRows([]);
    }
  }, []);

  useEffect(() => {
    load();
    api.get('/super-admin/companies').then((r) => setCompanies(r.data.companies || [])).catch(() => {});
  }, [load]);

  // Poll while a backup or a restore is still running.
  const active = (rows ?? []).some((r) => r.status === 'in_progress' || r.lastRestore?.status === 'running');
  useEffect(() => {
    if (!active) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [active, load]);

  const backupNow = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      await runnerApi.backupNow(target);
      await load();
    } catch (err) {
      setError(errorText(err, 'Backup failed to start'));
    } finally {
      setBusy(false);
    }
  };

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (rows ?? []).filter((r) => !q || `${r.companyName ?? ''} ${r.targetDb}`.toLowerCase().includes(q));
  }, [rows, filter]);

  const label = (r: BackupRow) => (r.targetDb === REGISTRY_TARGET ? 'Registry' : r.targetDb);

  return (
    <div className="space-y-4 px-6 py-6">
      {s3 && !s3.configured && (
        <Alert tone="warn" title="Backups are kept on one server's disk">
          BACKUP_S3_BUCKET isn't set on the API, so each backup lives only on the server that took it and is lost on redeploy.
          Set BACKUP_S3_* (bucket, region, keys; endpoint for S3-compatible stores) to keep them on S3, readable by every server.
        </Alert>
      )}
      <Panel
        title="Backups"
        actions={
          <>
            {s3?.configured && <Pill tone="ok">Stored on S3 · all servers see them</Pill>}
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter" className="w-32 rounded-lg border border-slate-300 px-2.5 py-1.5 text-[13px]" />
            {isOwner && (
              <>
                <select value={target} onChange={(e) => setTarget(e.target.value)} className="max-w-[220px] rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[13px]">
                  <option value="">Choose a database…</option>
                  <option value={REGISTRY_TARGET}>Registry</option>
                  {companies.filter((c) => c.database_name).map((c) => (
                    <option key={c.id} value={c.database_name!}>{c.company_name} ({c.database_name})</option>
                  ))}
                </select>
                <Btn onClick={backupNow} disabled={!target || busy}>{busy ? 'Starting…' : 'Back up now'}</Btn>
              </>
            )}
          </>
        }
        bodyClass="overflow-x-auto"
      >
        {rows === null ? (
          <div className="flex items-center gap-2 p-4 text-sm text-slate-500"><Spinner /> Loading…</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">Database</th><th className="px-3 py-2">Taken</th><th className="px-3 py-2">Why</th><th className="px-3 py-2">Size</th><th className="px-3 py-2">Checked</th><th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 align-top">
              {shown.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2">
                    <b className="text-slate-900">{label(r)}</b>
                    {r.companyName && r.targetDb !== REGISTRY_TARGET && <div className="text-[11px] text-slate-400">{r.companyName}</div>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">{when(r.completedAt || r.startedAt)}<div className="text-[11px] text-slate-400">{r.createdBy}</div></td>
                  <td className="px-3 py-2">{WHY[r.backupType] ?? r.backupType}</td>
                  <td className="whitespace-nowrap px-3 py-2">{bytes(r.sizeBytes)}</td>
                  <td className="px-3 py-2">
                    {r.status === 'in_progress' && <Pill tone="info"><Spinner className="h-3 w-3" /> backing up</Pill>}
                    {r.status === 'failed' && <Pill tone="bad" title={r.error ?? undefined}>failed</Pill>}
                    {r.status === 'expired' && <Pill tone="mut">expired</Pill>}
                    {r.status === 'completed' && (r.verifiedAt ? <Pill tone="ok">verified</Pill> : <Pill tone="mut">not checked</Pill>)}
                    {r.status === 'completed' && <span className="ml-1"><Pill tone={r.storage === 's3' ? 'ok' : 'warn'}>{r.storage === 's3' ? 'S3' : 'this server'}</Pill></span>}
                    {r.lastRestore && (
                      <div className="mt-1 text-[11.5px] text-slate-500">
                        Restore ({r.lastRestore.mode === 'copy' ? 'to a copy' : 'over live'}) {r.lastRestore.status}
                        {r.lastRestore.database && r.lastRestore.mode === 'copy' ? ` → ${r.lastRestore.database}` : ''}
                        {r.lastRestore.error ? `: ${r.lastRestore.error}` : ''} · {when(r.lastRestore.finishedAt || r.lastRestore.startedAt)}
                      </div>
                    )}
                    {r.status === 'failed' && r.error && <div className="mt-1 max-w-xs break-words text-[11.5px] text-red-700">{r.error.slice(0, 200)}</div>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    {r.fileName && r.storage === 'local' && (
                      <Btn kind="ghost" small onClick={() => downloadBackup(r.fileName!).catch((err) => setError(errorText(err, 'Download failed (the file may be on another server)')))}>Download</Btn>
                    )}{' '}
                    {isOwner && r.status === 'completed' && (
                      <Btn kind="ghost" small disabled={r.lastRestore?.status === 'running'} onClick={() => setRestoreOf(r)}>Restore…</Btn>
                    )}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">No backups yet.</td></tr>}
            </tbody>
          </table>
        )}
      </Panel>
      {error && <ErrorBox>{error}</ErrorBox>}

      <Panel
        title="Files on this server"
        actions={<Btn kind="ghost" small onClick={() => setShowFiles((v) => !v)}>{showFiles ? 'Hide' : 'Show'}</Btn>}
        bodyClass={showFiles ? 'p-4' : 'hidden'}
      >
        {showFiles && <Backups embedded />}
      </Panel>

      <RestoreDialog backup={restoreOf} onClose={() => setRestoreOf(null)} onStarted={() => { setRestoreOf(null); load(); }} />
    </div>
  );
};

const RestoreDialog: React.FC<{ backup: BackupRow | null; onClose: () => void; onStarted: () => void }> = ({ backup, onClose, onStarted }) => {
  const [mode, setMode] = useState<'copy' | 'replace'>('copy');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expected, setExpected] = useState<string | null>(null);
  const gate = useApprovalGate();

  useEffect(() => { setMode('copy'); setConfirm(''); setError(null); setExpected(null); }, [backup?.id]);
  if (!backup) return null;

  const live = expected ?? backup.liveDatabase;
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      // Over live: a second owner approves first (428 → "Ask another owner").
      const went = await gate.guard((approvalId) => runnerApi.restore(backup.id, mode, mode === 'replace' ? confirm.trim() : undefined, approvalId));
      if (went) onStarted();
    } catch (err) {
      const d = (err as { response?: { data?: { code?: string; expected?: string } } }).response?.data;
      if (d?.code === 'CONFIRM_MISMATCH' && d.expected) setExpected(d.expected);
      setError(errorText(err, 'Restore failed to start'));
    } finally {
      setBusy(false);
    }
  };

  const name = backup.targetDb === REGISTRY_TARGET ? 'the registry' : backup.targetDb;
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Restore ${name} to ${when(backup.completedAt || backup.startedAt)}?`}
      footer={
        <>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="danger" onClick={submit} disabled={busy || (mode === 'replace' && !confirm.trim())}>{busy ? 'Starting…' : 'Restore'}</Btn>
        </>
      }
    >
      {mode === 'replace' && (
        <Alert tone="bad" title={`Everything written after ${when(backup.completedAt || backup.startedAt)} is lost`}>
          A fresh backup of the current state is taken first so you can go back.
        </Alert>
      )}
      <Choice name="restore-mode" checked={mode === 'copy'} onChange={() => setMode('copy')} title={`Restore into a copy (${backup.targetDb === REGISTRY_TARGET ? 'registry' : backup.targetDb}_restore_…)`} recommended>
        Compare or pull rows from it; the live database isn't touched.
      </Choice>
      <Choice name="restore-mode" checked={mode === 'replace'} onChange={() => setMode('replace')} title="Replace the live database">
        Type <code className="rounded bg-slate-100 px-1">{live}</code> to confirm.
      </Choice>
      {mode === 'replace' && (
        <input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={live}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
          autoComplete="off"
        />
      )}
      <p className="text-xs text-slate-500">
        Needs a recent sign-in{mode === 'replace' ? ', and another owner approves a restore over the live database' : ''}. The restore runs in the background; its result shows on this row.
        {mode === 'replace' && backup.scope === 'tenant' && ' Its people see "Updating the system" until it finishes.'}
      </p>
      {error && <ErrorBox>{error}</ErrorBox>}
      {gate.element}
    </Dialog>
  );
};

export default BackupsTab;
